"use client";

import {
  getFontDefinition,
  type PdfFontKey,
} from "@/src/lib/pdf/fonts";
import { FontSelector } from "./font-selector";
import { FontSizeInput } from "./font-size-input";
import {
  PresetSelect,
  type PresetSelectOption,
} from "./preset-select";

export type FontAndSizeThemeProps<T extends string> = Readonly<{
  value: T;
  options: readonly PresetSelectOption<T>[];
  onChange: (value: T) => void;
  idPrefix: string;
  "aria-label": string;
  "aria-describedby"?: string;
}>;

export type FontAndSizeControlProps<T extends string = string> = Readonly<{
  fontFamily: PdfFontKey;
  fontSize: number;
  onFontFamilyChange: (fontFamily: PdfFontKey) => void;
  onFontSizeChange: (fontSize: number) => void;
  /** Section label used in aria attributes, e.g. "Editor" or "Document". */
  label: string;
  idPrefix: string;
  /** Optional theme select nested in the same control chrome. */
  theme?: FontAndSizeThemeProps<T>;
}>;

function FieldLabel({
  htmlFor,
  children,
}: Readonly<{
  htmlFor?: string;
  children: string;
}>) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-xs font-medium text-zinc-500"
    >
      {children}
    </label>
  );
}

export function FontAndSizeControl<T extends string = string>({
  fontFamily,
  fontSize,
  onFontFamilyChange,
  onFontSizeChange,
  label,
  idPrefix,
  theme,
}: FontAndSizeControlProps<T>) {
  const fontLabel = getFontDefinition(fontFamily).label;
  const sizeInputId = `${idPrefix}-size`;

  return (
    <div className="rounded-lg bg-zinc-50 p-2.5">
      <div className="flex w-full min-w-0 items-start gap-2.5">
        {theme ? (
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <FieldLabel>Theme</FieldLabel>
            <PresetSelect
              value={theme.value}
              options={theme.options}
              onChange={theme.onChange}
              idPrefix={theme.idPrefix}
              aria-label={theme["aria-label"]}
              aria-describedby={theme["aria-describedby"]}
              field
            />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <FieldLabel>Font</FieldLabel>
          <FontSelector
            value={fontFamily}
            onChange={onFontFamilyChange}
            field
            idPrefix={idPrefix}
            aria-label={`${label} font: ${fontLabel}`}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <FieldLabel htmlFor={sizeInputId}>Size</FieldLabel>
          <FontSizeInput
            id={sizeInputId}
            value={fontSize}
            onChange={onFontSizeChange}
            field
            label={null}
            aria-label={`${label} font size: ${fontSize} points`}
          />
        </div>
      </div>
    </div>
  );
}
